import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useEntitlementNamespaces} from 'src/hooks/dictionaries';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {createResource} from 'src/openapi/admin/apis/EntitlementsResourceApi';
import {ResourceType} from 'src/openapi/admin/models/ResourceType';
import {useAppDispatch} from 'src/store/hooks';

import {DEFAULT_RESOURCE,
    RESOURCE_DESCRIPTION_CONTROL,
    RESOURCE_DESCRIPTION_REQUIRED_ERROR,
    RESOURCE_DESCRIPTION_REQUIRED_MESSAGE,
    RESOURCE_EXTERNAL_ID_CONTROL,
    RESOURCE_EXTERNAL_ID_REQUIRED_ERROR,
    RESOURCE_EXTERNAL_ID_REQUIRED_MESSAGE,
    RESOURCE_METADATA_CONTROL,
    RESOURCE_NAME_CONTROL,
    RESOURCE_NAME_REQUIRED_ERROR,
    RESOURCE_NAME_REQUIRED_MESSAGE} from '../constants';
import {validateMetadata} from '../utils';

import type {CreateResourceRequest} from 'src/openapi/admin/models/CreateResourceRequest';


type Params = {
    isDataset?: boolean,
};

export const useCreateResource = ({isDataset}: Params = {}) => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext(ValidationContext);
    const {namespaces} = useEntitlementNamespaces();

    const getDispatchAction = useCallback(() => {
        const payload: {success: true, error: false, data: CreateResourceRequest} = {
            success: true,
            error: false,
            data: {
                ...DEFAULT_RESOURCE,
                namespace: namespaces.length ? namespaces[0].code : '',
                types: isDataset ? [ResourceType.DATASET] : undefined,
            },
        };
        return Promise.resolve({type: '', payload});
    }, [namespaces, isDataset]);

    const saveDispatchAction = useCallback(
        (createResourceRequest: CreateResourceRequest, signal: AbortSignal) => {
            // If metadata is empty or whitespace, set to undefined
            const cleanedRequest = {
                ...createResourceRequest,
                metadata: createResourceRequest.metadata && createResourceRequest.metadata.trim() ?
                    createResourceRequest.metadata : undefined,
            };
            return apiDispatch(
                createResource({createResourceRequest: cleanedRequest}, {signal}),
            );
        },
        [apiDispatch],
    );

    const editValidation = useCallback((data: CreateResourceRequest) => {
        if (data.id) {
            removeError(RESOURCE_EXTERNAL_ID_CONTROL);
        }

        if (data.name) {
            removeError(RESOURCE_NAME_CONTROL);
        }

        if (data.description) {
            removeError(RESOURCE_DESCRIPTION_CONTROL);
        }


        if (!data.metadata || (data.metadata.length <= 1024 && (() => {
            try {
                if (!data.metadata.trim()) {
                    return true;
                } JSON.parse(data.metadata); return true;
            } catch {
                return false;
            }
        })())) {
            removeError(RESOURCE_METADATA_CONTROL);
        }
    }, [removeError]);

    const saveValidation = useCallback((data: CreateResourceRequest) => {
        let valid = true;

        if (!data.id) {
            addError({
                controlId: RESOURCE_EXTERNAL_ID_CONTROL,
                code: RESOURCE_EXTERNAL_ID_REQUIRED_ERROR,
                message: RESOURCE_EXTERNAL_ID_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!data.name) {
            addError({
                controlId: RESOURCE_NAME_CONTROL,
                code: RESOURCE_NAME_REQUIRED_ERROR,
                message: RESOURCE_NAME_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!data.description) {
            addError({
                controlId: RESOURCE_DESCRIPTION_CONTROL,
                code: RESOURCE_DESCRIPTION_REQUIRED_ERROR,
                message: RESOURCE_DESCRIPTION_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!validateMetadata(data.metadata, addError)) {
            valid = false;
        }

        return valid;
    }, [addError]);

    return {
        ...useCommonDataHook({
            initValue: {
                ...DEFAULT_RESOURCE,
                namespace: namespaces.length ? namespaces[0].code : '',
                types: isDataset ? [ResourceType.DATASET] : undefined,
            },
            getDispatchAction,
            saveDispatchAction,
            editValidation,
            saveValidation,
        }),
        namespaces,
    };
};
